/**
 * Audio input selection rules for an agent console.
 *
 * Pure decision logic, kept out of the browser-bound device manager so it can
 * actually be tested (studio has no test target). It exists because letting the
 * browser pick put a SILENT virtual device on every call: Chrome defaulted to
 * BlackHole, a loopback endpoint with nothing routed into it, and the agent
 * published pure silence while every other signal reported healthy
 * (2026-08-03).
 */

/**
 * Substrings of device labels that are loopback/routing endpoints rather than
 * microphones. Never auto-selected — they are silent unless the agent has
 * deliberately routed audio into them.
 */
const VIRTUAL_INPUT_HINTS = ["blackhole", "loopback", "soundflower", "virtual", "teams audio", "aggregate", "zoomaudiodevice", "obs"];

export interface AudioDeviceOption {
  deviceId: string;
  label: string;
  /** True when the label looks like a virtual routing device, not a mic. */
  isVirtual: boolean;
}

export function isVirtualInputLabel(label: string): boolean {
  const lower = label.toLowerCase();
  return VIRTUAL_INPUT_HINTS.some((hint) => lower.includes(hint));
}

/**
 * Choose an input: the agent's remembered choice if still present, else the
 * system default, else the first REAL microphone. A virtual device is only
 * chosen when nothing else exists — or when the agent picked it themselves,
 * which is a deliberate act and is honoured.
 */
export function pickDefaultInput(options: AudioDeviceOption[], remembered?: string | null): string | undefined {
  if (!options.length) return undefined;
  if (remembered && options.some((o) => o.deviceId === remembered)) return remembered;
  const systemDefault = options.find((o) => o.deviceId === "default" && !o.isVirtual);
  if (systemDefault) return systemDefault.deviceId;
  const firstReal = options.find((o) => !o.isVirtual);
  return (firstReal ?? options[0]).deviceId;
}
